import { computed, h } from 'vue'
import { useRouter } from 'vue-router'
import { NAvatar, NText } from 'naive-ui'
import defaultAvatar from '@/assets/avatar.jpg'
import { useAppStore, useAuthStore, useUserStore } from '@/store'
import { transformMinioUrl } from '@/utils/functions'
import { t } from '@/locales'
import { AppSvgEIcon, SvgIcon } from '@/components/common'
import englishIcon from '@/assets/classroom-v2/english.svg'
import englishIconDark from '@/assets/classroom-v2/englishDark.svg'
import vietnameseIcon from '@/assets/classroom-v2/vietnamese.svg'
import vietnameseIconDark from '@/assets/classroom-v2/vietnameseDark.svg'
import { isUserRole } from '@/constants/role'
import { useIframeTheme } from '@/composables/useIframeTheme'

export function useAccountDropdown() {
  const userStore = useUserStore()
  const router = useRouter()
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const { sendThemeToIframes } = useIframeTheme()

  const userInfo = computed(() => userStore.userInfo)
  const userRole = computed(() => userStore.role)
  const theme = computed(() => appStore.theme)
  const language = computed(() => appStore.language)

  const renderCustomHeader = () => {
    return h(
      'div',
      {
        class: 'p-2 flex items-center cursor-pointer',
      },
      [
        h(NAvatar, {
          round: true,
          style: 'margin-right: 12px;',
          class: 'h-8 w-8',
          src: userInfo.value.icon_url ? transformMinioUrl(userInfo.value.icon_url) : defaultAvatar,
        }),
        h('div', null, [
          h('div', null, [
            h(NText, { depth: 2 }, {
              default: () => userInfo.value.fullName || '',
            }),
          ]),
          h('div', { style: 'font-size: 12px;' }, [
            h(NText, { depth: 3 }, { default: () => userInfo?.value?.email }),
          ]),
        ]),
      ],
    )
  }

  const createImgIcon = (src: string, alt: string, classname: any = '') => () =>
    h('img', {
      src,
      alt,
      class: `${classname} w-5 h-5 object-contain`,
    })

  const dropdownOptions = computed(() => [
    {
      key: 'header',
      type: 'render',
      render: renderCustomHeader,
    },
    {
      type: 'divider',
      key: 'd1',
    },
    {
      label: t('settings.userManagementDropdown.workspaceInfo'),
      key: 'workspace-settings',
      icon: () => h(SvgIcon, { icon: 'streamline-flex:school', size: '20' }),
      show: isUserRole(userRole.value, 'ADMIN'),
    },
    {
      label: t('settings.userManagementDropdown.classInfo'),
      key: 'class-info',
      icon: () => h(AppSvgEIcon, { name: 'element-4', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
      show: isUserRole(userRole.value, 'ADMIN'),
    },
    {
      label: userStore?.isFirstLogin ? t('auth.setup_password') : t('auth.change_password'),
      key: 'set-password',
      icon: () => h(AppSvgEIcon, { name: 'security-safe', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
    },
    {
      label: 'English',
      key: 'english',
      icon: createImgIcon(theme.value === 'dark' ? englishIconDark : englishIcon, 'English flag'),
      show: language.value === 'en-US',
    },
    {
      label: 'Tiếng Việt',
      key: 'vietnamese',
      icon: createImgIcon(theme.value === 'dark' ? vietnameseIconDark : vietnameseIcon, 'Vietnamese flag'),
      show: language.value === 'vi-VN',
    },
    {
      label: t('common.light'),
      key: 'light',
      icon: () => h(SvgIcon, {
        icon: 'mynaui:cloud-sun',
        size: '20',
      }),
      show: theme.value === 'light',
    },
    {
      label: t('common.dark'),
      key: 'dark',
      icon: () => h(SvgIcon, {
        icon: 'solar:moon-fog-outline',
        size: '20',
      }),
      show: theme.value === 'dark',
    },
    {
      label: () =>
        h('span', { class: 'text-[#F14933]' }, t('settings.userManagementDropdown.logout')),
      key: 'logout',
      icon: () => h(AppSvgEIcon, { name: 'login', size: 24, color: '#F14933' }),
    },
  ])

  const handleSelect = (key: string, callbacks: {
    onViewProfile?: () => void
    onClassInfo?: () => void
    onWorkspaceInfo?: () => void
    onSetPassword?: () => void
    onLogout?: () => void
  } = {}) => {
    switch (key) {
      case 'class-info':
        callbacks.onClassInfo?.()
        break
      case 'workspace-settings':
        callbacks.onWorkspaceInfo?.() || router.push({ name: 'classroom.workspace' })
        break
      case 'set-password':
        callbacks.onSetPassword?.()
        break
      case 'logout':
        callbacks.onLogout?.() || authStore.logout()
        break
      case 'english':
        appStore.setLanguage('vi-VN')
        break
      case 'vietnamese':
        appStore.setLanguage('en-US')
        break
      case 'light':
        appStore.setTheme('dark')
        sendThemeToIframes('dark')
        break
      case 'dark':
        appStore.setTheme('light')
        sendThemeToIframes('light')
        break
    }
  }

  return {
    dropdownOptions,
    handleSelect,
    userInfo,
  }
}
